import { useEffect, useRef } from 'react';
import { useStore } from '../store/useStore';

const VERSION_CHECK_INTERVAL = 60000; // Controlla ogni 60 secondi
const VERSION_STORAGE_KEY = 'app-version';
const VERSION_NOTIFICATION_TITLE = 'Nuova versione disponibile';

export function useVersionCheck() {
  const addNotification = useStore((state) => state.addNotification);
  const removeNotification = useStore((state) => state.removeNotification);
  const setUpdateAvailable = useStore((state) => state.setUpdateAvailable);
  const checkIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    // Ottieni la versione corrente dal build (definita in vite.config.ts)
    const buildVersion = (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0') as string;

    // Rimuovi eventuali notifiche di versione esistenti all'avvio
    const removeVersionNotifications = () => {
      const state = useStore.getState();
      state.notifications
        .filter((n) => n.title === VERSION_NOTIFICATION_TITLE)
        .forEach((n) => state.removeNotification(n.id));
    };

    // Salva la versione corrente e rimuovi notifiche se la versione è aggiornata
    const savedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
    if (!savedVersion || savedVersion !== buildVersion) {
      // Versione cambiata o prima volta: aggiorna localStorage e rimuovi notifiche
      localStorage.setItem(VERSION_STORAGE_KEY, buildVersion);
      // Usa setTimeout per assicurarsi che lo store sia inizializzato
      setTimeout(() => {
        removeVersionNotifications();
        setUpdateAvailable(false);
      }, 100);
    } else {
      // Versione corrisponde: rimuovi comunque eventuali notifiche rimaste
      setTimeout(() => {
        removeVersionNotifications();
        setUpdateAvailable(false);
      }, 100);
    }

    // Funzione per controllare se c'è una nuova versione
    const checkForUpdate = async () => {
      try {
        // Controlla il file version.json con cache busting
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
          },
        });

        if (!response.ok) {
          // Se il file non esiste, usa l'approccio alternativo: controlla l'ETag di index.html
          const htmlResponse = await fetch(`/?t=${Date.now()}`, {
            cache: 'no-store',
            method: 'HEAD',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
            },
          });

          if (!htmlResponse.ok) return;

          const etag = htmlResponse.headers.get('etag') || htmlResponse.headers.get('last-modified');
          const savedEtag = localStorage.getItem(`${VERSION_STORAGE_KEY}-etag`);

          if (etag && etag !== savedEtag && savedEtag !== null) {
            // Nuova versione rilevata tramite ETag
            showUpdateNotification();
            setUpdateAvailable(true);
            localStorage.setItem(`${VERSION_STORAGE_KEY}-etag`, etag);
          } else if (!savedEtag && etag) {
            // Prima volta, salva l'ETag
            localStorage.setItem(`${VERSION_STORAGE_KEY}-etag`, etag);
          }
          return;
        }

        const data = await response.json();
        const serverVersion = data.version;
        const currentSavedVersion = localStorage.getItem(VERSION_STORAGE_KEY) || buildVersion;

        if (serverVersion && serverVersion !== currentSavedVersion) {
          // Nuova versione disponibile sul server
          showUpdateNotification();
          setUpdateAvailable(true);
        } else if (serverVersion && serverVersion === currentSavedVersion) {
          // Versione corrisponde: rimuovi eventuali notifiche
          removeVersionNotifications();
          setUpdateAvailable(false);
        }
      } catch (error) {
        // Silenziosamente ignora errori di rete
        console.debug('Version check failed:', error);
      }
    };

    const showUpdateNotification = () => {
      // Rimuovi eventuali notifiche di versione esistenti (cerca per titolo)
      removeVersionNotifications();

      // Aggiungi la nuova notifica persistente
      addNotification({
        type: 'info',
        title: VERSION_NOTIFICATION_TITLE,
        message: 'È disponibile una nuova versione dell\'applicazione. Clicca su Aggiorna per caricare le ultime modifiche.',
        persistent: true,
        action: {
          label: 'Aggiorna',
          onClick: () => {
            // Rimuovi la notifica prima del reload
            removeVersionNotifications();
            setUpdateAvailable(false);

            // Hard refresh: forza il browser a ricaricare tutto dalla cache del server
            // Rimuovi service worker se presente per evitare problemi di cache
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.getRegistrations().then((registrations) => {
                Promise.all(registrations.map(reg => reg.unregister())).then(() => {
                  // Hard reload bypassando cache
                  window.location.reload();
                });
              });
            } else {
              // Hard refresh standard
              window.location.reload();
            }
          },
        },
      });
    };

    // Controlla immediatamente al mount (dopo un breve delay per permettere al componente di montare)
    const initialCheckTimeout = setTimeout(checkForUpdate, 1000);

    // Imposta l'intervallo per controlli periodici
    checkIntervalRef.current = window.setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);

    // Cleanup
    return () => {
      clearTimeout(initialCheckTimeout);
      if (checkIntervalRef.current !== null) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [addNotification, removeNotification, setUpdateAvailable]);

  return null;
}
